import React, { useEffect, useState } from "react";
// Constants
import { supportRequestStatus } from "../../constants/commonConstants";

// Assets
import { X } from "lucide-react";
import CustomButton from "../Form/CustomButtonWithHover";
import { CiCalendar } from "react-icons/ci";
import { IoMdTime } from "react-icons/io";
import { PiCalculatorLight } from "react-icons/pi";
import { MdOutlineCall } from "react-icons/md";
import { IoPersonOutline } from "react-icons/io5";
import { Support } from "@mui/icons-material";

const SupportRequestDetailsModal = ({
  isOpen,
  onCancel,
  onConfirm,
  supportLogDetails,
}) => {
  const [status, setStatus] = useState(supportLogDetails.status); // set Status for testing purpose

  // Convert status like "IN_PROGRESS" to "In Progress"
  const displayStatus = status
    ?.toLowerCase()
    ?.split("_")
    ?.map((word) => word?.charAt(0)?.toUpperCase() + word?.slice(1))
    ?.join(" ");

  useEffect(() => {
    setStatus(supportLogDetails?.status);
  }, [supportLogDetails?.status, supportLogDetails]);

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      {/* Modal Container */}
      <div className="bg-white rounded-xl max-w-[495px] w-[90%] py-4 px-3 [@media(min-width:500px)]:p-[30px] shadow-lg relative font-inter">
        {/* Close Button */}
        <button
          onClick={onCancel}
          className="absolute text-black right-5 top-4"
        >
          <X size={24} />
        </button>

        {/* Status tag */}
        <div
          className={`{/* supportLogDetails.status */} ${
            status === supportRequestStatus.COMPLETED
              ? "border-green-action bg-[#07C01D] bg-opacity-15 text-green-action"
              : "border-orange-action border-opacity-30 bg-[#DEA156] bg-opacity-15 text-orange-action"
          } border-[0.5px] text-xs leading-[20px] rounded-[10px] capitalize w-[87px] h-[23px] text-center`}
        >
          {displayStatus || ""}
        </div>

        {/* Title */}
        <div className="text-dark-blue text-lg font-semibold leading-[22px] mt-2">
          Support Request Details
        </div>

        {/* Details */}
        <div
          className="border border-[#FAFAFA] rounded-[5px] p-3 sm:p-[20px] mt-5 sm:mt-10 gap-4 sm:gap-[25px] grid grid-cols-1 [@media(min-width:460px)]:grid-cols-2"
          style={{ boxShadow: "2px 4px 4px 0px #0000000A" }}
        >
          <div className="flex flex-col gap-[15px]">
            <div className="flex flex-col">
              <div className="flex flex-row gap-3 text-xs text-[#777777]">
                <CiCalendar size={14} /> Submitted On
              </div>
              <div className="ml-7 text-[#5E5E5E] text-sm">
                {supportLogDetails?.submitDate || "N/A"}
              </div>
            </div>
            <div className="flex flex-col">
              <div className="flex flex-row gap-3 text-xs text-[#777777]">
                <PiCalculatorLight size={14} /> Landlord
              </div>
              <div className="ml-7 text-[#5E5E5E] text-sm">
                {supportLogDetails?.landlordName || "N/A"}
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-[15px]">
            <div className="flex flex-col">
              <div className="flex flex-row gap-3 text-xs text-[#777777]">
                <IoMdTime size={14} /> Request Type
              </div>
              <div className="ml-7 text-[#5E5E5E] text-sm capitalize">
                {supportLogDetails?.requestType || "N/A"}
              </div>
            </div>
            <div className="flex flex-col">
              <div className="flex flex-row gap-3 text-xs text-[#777777]">
                <MdOutlineCall size={14} /> Contact
              </div>
              <div className="ml-7 text-[#5E5E5E] text-sm">
                {supportLogDetails?.contact?.email} <br />{" "}
                {supportLogDetails?.contact?.phone}
              </div>
            </div>
          </div>
        </div>

        <div
          className="mt-[15px] px-[20px] py-4 border border-[#FAFAFA] rounded-[5px]"
          style={{ boxShadow: "2px 4px 4px 0px #0000000A" }}
        >
          <div className="text-[#303030] font-medium text-sm">
            Related Property
          </div>
          <div className="grid items-start justify-between grid-cols-1 [@media(min-width:460px)]:grid-cols-2 gap-3 mt-2 sm:flex-row sm:items-center">
            <div className="flex flex-col">
              <div className="flex flex-row gap-3 text-xs text-[#777777]">
                <PiCalculatorLight size={14} /> Unit ID:
              </div>
              <div className="ml-7 text-[#5E5E5E] text-sm">
                {supportLogDetails?.unitId || "N/A"}
              </div>
            </div>
            <div className="flex flex-col">
              <div className="flex flex-row gap-3 text-xs text-[#777777]">
                <IoPersonOutline size={14} />
                Tenant:
              </div>
              <div className="ml-7 text-[#5E5E5E] text-sm">
                {supportLogDetails.tenantName}
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="sm:mt-[30px] mt-[15px] max-h-[120px] overflow-y-auto">
          <div className="mb-2 text-base font-semibold">
            {supportLogDetails?.subject}
          </div>
          <div className="text-[#727272] text-[13px]">
            {supportLogDetails?.description || "No description provided."}
          </div>
        </div>

        {/* Button Section */}
        <div className="flex flex-row items-center justify-center mt-5 sm:mt-11">
          <CustomButton
            type="button"
            buttonText="CLOSE"
            bgColor="bg-white"
            textColor="text-[#873FE2]"
            textWeight="font-medium"
            className="w-[64px] font-inter h-9 text-[12px] font-medium rounded-md border border-[#873FE2]"
            onClick={onCancel}
          />
          {!(status === supportRequestStatus.COMPLETED) && (
            <CustomButton
              type="button"
              buttonText="MARK AS COMPLETED"
              bgColor="bg-[#873FE2]"
              textColor="text-white"
              textWeight="font-medium"
              className="w-[157px] h-9 text-[12px] font-inter rounded-md ml-2 mr-2"
              onClick={onConfirm}
              // onClick={() => {
              //   setStatus("completed");
              // }} // set status for testing purpose
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default SupportRequestDetailsModal;
